import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form, Divider, Button, Message, FormField, Checkbox } from 'semantic';
import { ApiError, request } from '../../../../utils/api';
import FormVerticalSpace from '../../components/atoms/FormVerticalSpace';
import { useUser } from '../../../../contexts/user';
import CheckboxField from '../../../../components/form-fields/formik/CheckboxField';
import SelectField from '../../../../components/form-fields/formik/SelectField';
import { isAccountAdmin } from '../../../../utils/roles';
import { getNotifyEvseRecipientOptions } from '../../../../helpers/account';
import SettingsShell from '../../components/wrappers/SettingsShell';
import FormHeader from 'screens/Settings/components/atoms/FormHeader';
import FeatureFlag from 'components/FeatureFlag';

interface NotificationsFormValues {
  account: {
    notifyEVSEIssues: boolean;
    notifyEVSEIssuesRecipient: string;
  };
}

type UserNotificationSubscription = {
  _id?: string;
  userId: string;
  address: {
    channel: 'email';
    adapter: 'postmark';
    email: string;
  };
  locale: string;
  topics: string[];
};

export default function Notifications() {
  return (
    <SettingsShell>
      <NotificationSettingsWrapper />
    </SettingsShell>
  );
}

function NotificationSettingsWrapper() {
  const { t } = useTranslation();

  const submitChargingStationSettingsRef = useRef<HTMLButtonElement | null>(
    null
  );
  const submitSessionSettingsRef = useRef<HTMLButtonElement | null>(null);

  return (
    <>
      <ChargingStationNotifications
        submitRef={submitChargingStationSettingsRef}
      />
      <FeatureFlag feature="charging_session_ended_email">
        <ChargingSessionsNotifications submitRef={submitSessionSettingsRef} />
      </FeatureFlag>
      <Button
        style={{ marginLeft: 0 }}
        onClick={() => {
          if (submitChargingStationSettingsRef.current) {
            submitChargingStationSettingsRef.current.click();
          }

          if (submitSessionSettingsRef.current) {
            submitSessionSettingsRef.current.click();
          }
        }}>
        {t(
          'settings.personal.notifications.updateNotifications',
          'Update Notifications'
        )}
      </Button>
    </>
  );
}

type ChargingSessionsNotificationsProps = {
  submitRef: React.RefObject<HTMLButtonElement>;
};
function ChargingSessionsNotifications({
  submitRef,
}: ChargingSessionsNotificationsProps) {
  const { user } = useUser();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [currentSubscription, setCurrentSubscription] = useState<
    UserNotificationSubscription | undefined
  >();

  if (!user) {
    return null;
  }

  const getNotifications = async () => {
    setLoading(true);
    request<{ data: UserNotificationSubscription }>({
      method: 'GET',
      path: '/1/users/me/notifications/subscriptions',
      params: {
        channel: 'email',
        adapter: 'postmark',
        email: user.email!,
      },
    })
      .then((response) => {
        setCurrentSubscription(response.data);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        setError(e.message);
      });
  };

  const patchNotificationSubscription = async (
    notificationSub: UserNotificationSubscription
  ) => {
    const topics: { topic: string; enabled: boolean }[] = [];
    if (notificationSub.topics.includes('charging.session.ended')) {
      topics.push({ topic: 'charging.session.ended', enabled: true });
    } else {
      topics.push({ topic: 'charging.session.ended', enabled: false });
    }

    const req = { address: notificationSub.address, topics };

    return request<UserNotificationSubscription>({
      method: 'PATCH',
      path: `/1/users/me/notifications/subscriptions`,
      body: req,
    })
      .then((response) => {
        setCurrentSubscription(response);
        return response;
      })
      .catch((e) => {
        setError(e.message);
      });
  };

  useEffect(() => {
    getNotifications();
  }, [user.id]);

  const handleSubmit = async (value: Partial<UserNotificationSubscription>) => {
    if (value._id) {
      await patchNotificationSubscription(
        value as UserNotificationSubscription
      );
    } else {
      setError(
        t(
          'settings.personal.notifications.notificationSubscriptionWasNotFound',
          'Notification subscription was not found'
        )
      );
      return;
    }

    await getNotifications();
  };

  if (loading) {
    return <div />;
  }

  if (!currentSubscription) {
    return (
      <Message error>
        {t(
          'settings.personal.notifications.notificationSubscriptionWasNotFound',
          'Notification subscription was not found'
        )}
      </Message>
    );
  }

  return (
    <>
      <br />
      <Formik initialValues={currentSubscription} onSubmit={handleSubmit}>
        {({ handleSubmit, isSubmitting, values, setFieldValue, dirty }) => {
          return (
            <Form onSubmit={handleSubmit} loading={isSubmitting}>
              <FormHeader
                size="medium"
                mt={20}
                mb={15}
                text={t(
                  'settings.personal.notifications.sessions.title',
                  'Sessions'
                )}
              />

              <FormField>
                <Checkbox
                  toggle
                  checked={
                    values.topics &&
                    values.topics.includes('charging.session.ended')
                  }
                  onChange={(_: unknown, { checked }: { checked: boolean }) => {
                    if (checked) {
                      const newTopics = structuredClone(values.topics || []);
                      if (!newTopics.includes('charging.session.ended')) {
                        newTopics.push('charging.session.ended');
                      }
                      setFieldValue('topics', newTopics);
                    } else {
                      const newTopics = structuredClone(values.topics);
                      if (!newTopics) {
                        return;
                      }
                      newTopics.splice(
                        newTopics.indexOf('charging.session.ended'),
                        1
                      );

                      setFieldValue('topics', newTopics);
                    }
                  }}
                  label={t(
                    'settings.personal.notifications.sessions.notifyOnSessionEnd',
                    'Send notifications when my charging sessions end.'
                  )}
                />
              </FormField>

              <Button
                type="submit"
                style={{ marginLeft: 0, display: 'none' }}
                disabled={!dirty}
                ref={submitRef}>
                {t(
                  'settings.personal.notifications.updateNotifications',
                  'Update Notifications'
                )}
              </Button>
            </Form>
          );
        }}
      </Formik>

      {error && <Message error>{error}</Message>}
    </>
  );
}

type ChargingStationNotificationsProps = {
  submitRef: React.RefObject<HTMLButtonElement>;
};
function ChargingStationNotifications({
  submitRef,
}: ChargingStationNotificationsProps) {
  const { user, fetchUser, provider } = useUser();
  const { t } = useTranslation();

  const [formValues, setFormValues] = React.useState({
    user,
    account: { notifyEVSEIssues: true, notifyEVSEIssuesRecipient: 'evseOwner' },
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getNotificationSettings();
  }, []);

  async function getNotificationSettings() {
    if (!user || !isAccountAdmin(user)) {
      return;
    }
    try {
      const response = await request({
        method: 'GET',
        path: '/1/accounts/self',
      });
      setFormValues((state) => ({ ...state, account: response.data }));
    } catch (error: any) {
      setErrorMessage(error.message);
    }
  }

  const handleSubmit = async (values: NotificationsFormValues) => {
    try {
      await request({
        method: 'PATCH',
        path: '/1/users/me',
        body: values.user,
      });
      if (isAccountAdmin(user)) {
        await request({
          method: 'PATCH',
          path: '/1/accounts/self',
          body: values.account,
        });
      }
      await fetchUser();
      setSuccess(true);
      setErrorMessage('');
    } catch (error: any) {
      setSuccess(false);
      setErrorMessage(error.message);
    }
  };

  const notifyEVSEIssuesRecipientOptions = useMemo(() => {
    return getNotifyEvseRecipientOptions(t).map((option) => ({
      key: option.text,
      value: option.value,
      text: option.text,
    }));
  }, [t]);
  return (
    <Formik
      initialValues={{
        account: {
          notifyEVSEIssues: formValues.account.notifyEVSEIssues,
          notifyEVSEIssuesRecipient:
            formValues.account.notifyEVSEIssuesRecipient,
        },
      }}
      onSubmit={handleSubmit}
      enableReinitialize>
      {({ handleSubmit, isSubmitting, values, dirty }) => {
        return (
          <Form onSubmit={handleSubmit} loading={isSubmitting}>
            <FormHeader
              size="large"
              pb={5}
              pt={5}
              text={t('settings.personal.notifications.title', 'Notifications')}
            />
            <Divider />
            {errorMessage && <Message error content={errorMessage} />}
            {success && (
              <Message
                success
                content={t('messages.updated', 'Successfully updated!')}
              />
            )}
            <p>
              {t(
                'settings.personal.notifications.description_v2',
                'Select and manage notifications you would like to receive from {{provider}}',
                {
                  provider: provider?.name,
                }
              )}
            </p>

            {isAccountAdmin(user) && (
              <>
                <FormHeader
                  size="medium"
                  mt={20}
                  mb={15}
                  text={t(
                    'settings.personal.notifications.subHeader.chargingStations',
                    'Charging Stations'
                  )}
                />

                <CheckboxField
                  name="account.notifyEVSEIssues"
                  label={t(
                    'settings.personal.notifications.notifyEVSEIssues',
                    'Send notifications when my charging stations are experiencing problems.'
                  )}
                  toggle
                />

                <FormVerticalSpace />

                {values.account.notifyEVSEIssues && (
                  <SelectField
                    name="account.notifyEVSEIssuesRecipient"
                    label={t(
                      'settings.personal.notifications.notifyEVSEIssuesRecipient',
                      'Send notifications to'
                    )}
                    options={notifyEVSEIssuesRecipientOptions}
                    required
                  />
                )}
              </>
            )}

            <Button
              type="submit"
              style={{ marginLeft: 0, display: 'none' }}
              disabled={!dirty}
              ref={submitRef}>
              {t(
                'settings.personal.notifications.updateNotifications',
                'Update Notifications'
              )}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
}
